import { Component, OnInit, ViewChild, Output, EventEmitter, Input } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AppSettings } from "../../../../../shared/app.settings";
import type { Country } from "../../../../../shared/models/country";
import type { Language } from "../../../../../shared/models/language";
import { CommonBindingDataService } from "../../../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../../../shared/services/entity.service";
import type { AttributeData, entityResponse } from "app/modules/shared/models/attribute.models";
import { ConfigService } from "app/modules/shared/services/config.service";
import type { EntityResponse } from "app/modules/shared/models/entityResponse.models";
import { MiFormComponent } from "app/modules/shared/components/mi-form/mi-form.component";

@Component({
  selector: 'app-add-delivery-address',
  standalone: true,
  imports: [
    BreadcrumbModule,
    TabViewModule,
    MiFormComponent,
    DialogModule,
    ButtonModule,
    TranslateModule,
    ProgressSpinnerModule
  ],
  templateUrl: './add-delivery.component.html',
  styleUrl: './add-delivery.component.scss'
})
export class AddDeliveryAddressComponent implements OnInit {
  activeIndex: number = 0;
  data: AttributeData;
  siteAddressAttributeData: any;
  originalSiteAddressAttributeData: any;
  routePath: MenuItem[] = [];
  attributeLabels = {};
  moduleName: string;
  country: Country;
  language: Language;
  relation: string = "oneToOne";
  cols: any = [];
  siteAddressAttributeLength;
  siteAddressIdForDocument: string;
  vehicleDocumentId;
  siteDeliveryAddressRelation: any;
  attributeValues = [];
  nextBtnLabel;
  previousBtnLabel;
  label: string;
  initialLabel: string = this.cs.getLabel("lbl_details");
  saveSiteAddressBtnLabel: string;
  isFirstTabSaved: boolean = false;
  isNew: boolean = true;
  prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX
  tabCode;
  tabIndex;
  @Input() siteEntityId: string;
  @Input() siteAddressId: string;
  @Input() recordCount: number = 0;
  isLoading: boolean = true;

  @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
  @Output() saveSuccess = new EventEmitter<void>();

  constructor(
    private entityService: EntityService,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private configService: ConfigService
  ) { }

  ngOnInit() {
    this.retrieveSiteAddressId();
    this.initializeLabels();
    this.initializeEntityService();
    if (this.siteAddressId) {
      this.loadSiteAddressData();
    } else {
      this.getAttributes();
    }
    this.setRoutePath('details');
    this.isFirstTabSaved = false;
    this.activeIndex = 0;
  }

  private initializeLabels() {
    this.nextBtnLabel = this.siteAddressId ?
      this.cs.getLabel("site.lbl_update_delivery_address") :
      this.cs.getLabel("site.lbl_add_delivery_address");
    this.previousBtnLabel = this.cs.getLabel("lbl_previous");
    this.saveSiteAddressBtnLabel = this.siteAddressId ?
      this.cs.getLabel("site.lbl_update_delivery_address") :
      this.cs.getLabel("site.lbl_add_delivery_address");
  }

  private retrieveSiteAddressId() {
    if (this.siteAddressId) {
      this.siteAddressIdForDocument = this.siteAddressId;
    }
  }

  private initializeEntityService() {
    this.moduleName = AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS;
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.entityService.setRelatedData([]);
  }

  private loadSiteAddressData() {
    this.isLoading = true;
    if (this.siteAddressId) {
      this.entityService
        .getEntity(this.siteAddressId, AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
        .subscribe({
          next: (res: EntityResponse) => {
            if (res) {
              this.processSiteAddressResponse(res);
              this.isNew = false;
              const currentTab = this.siteAddressAttributeData?.tabs[this.activeIndex];
              if (currentTab) {
                this.setRoutePath(currentTab.tabCode);
              }
              this.getAttributes();
            }
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
    } else {
      this.getAttributes();
    }
  }

  private processSiteAddressResponse(data: any) {
    this.attributeValues = this.cs.getOrgAttributeValues(data);
    this.normalizeManufacturingYear();
    this.processRelatedData(data.relatedData);
    this.getAttributes();
  }

  private normalizeManufacturingYear() {
    const yearOfMnfgAttributeCode = "year_of_manufacturing";
    if (this.attributeValues[yearOfMnfgAttributeCode]) {
      this.attributeValues[yearOfMnfgAttributeCode] = new Date(
        this.attributeValues[yearOfMnfgAttributeCode], 0
      );
    }
  }

  private processRelatedData(relatedData: any[]) {
    if (relatedData.length > 0) {
      const organizedData: { [key: string]: any[] } = {};
      relatedData.forEach(item => {
        if (item.actionStatus === "DELETED") {
          return;
        }
        const entityCode = item.entityCode;
        if (!organizedData[entityCode]) {
          organizedData[entityCode] = [];
        }
        const entityData = this.cs.getOrgAttributeValues(item) as any;
        entityData.entityId = item.entityId;
        organizedData[entityCode].push(entityData);
      });
      this.entityService.setRelatedData(organizedData);
    }
  }

  onTabChange(event: any) {
    const currentTab = this.siteAddressAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (event.index === 0) {
      this.isFirstTabSaved = false;
    }
    if (!this.isFirstTabSaved && event.index > 0) {
      this.activeIndex = 0;
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please save details in first tab before proceeding'
      });
      return;
    }

    this.label = event.originalEvent.target.innerText.trim();
  }

  getAttributes() {
    const entityType = AppSettings.ENTITY_CODE.SITE_DELIVERY_ADDRESS;
    const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;

    this.entityService.getAttributeDefinition(entityType, viewCode).subscribe({
      next: (res) => {
        if (!res) {
          this.isLoading = false;
          return;
        }
        this.setSiteAddressView(res);
        const currentTab = this.siteAddressAttributeData.tabs[this.activeIndex];
        if (currentTab) {
          this.setRoutePath(currentTab.tabCode);
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  setSiteAddressView(res) {
    this.attributeLabels = this.cs.getAttributeLabels(res);
    this.originalSiteAddressAttributeData = this.cs.getOrganizedAttribute(res);
    this.siteAddressAttributeData = _.cloneDeep(this.originalSiteAddressAttributeData);

    this.siteAddressAttributeData.tabs = this.siteAddressAttributeData.tabs.map(tab => ({
      ...tab,
      groups: tab.groups.map(group => ({
        ...group,
        fields: group.fields.map(field => {
          if (field.attributeCode === 'default_address') {
            return {
              ...field,
              presetValues: [{ labelKey: "Yes", labelValue: "Yes", parentLabelKey: null, color: null, iconUrl: null }]
            };
          }
          return field;
        }).filter(field => field.attributeCode !== 'site_entity_id')
      }))
    }));

    this.siteAddressAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.siteAddressAttributeData?.tabs);
    this.tabIndex = this.siteAddressAttributeData.tabs;
    this.tabIndex.forEach(tab => {
      this.setRoutePath(tab.tabCode);
    });
    this.siteAddressAttributeLength = this.siteAddressAttributeData.tabs.length;
    this.siteDeliveryAddressRelation = _.find(res?.relations, { otherEntityCode: 'site', relation: "oneToOne" });
  }

  private setDateFieldValidation(tabCode, groupCode, attributeCode, isMax) {
    const tab = this.siteAddressAttributeData.tabs.find(ele => ele.tabCode === tabCode);
    const group = tab?.groups.find(ele => ele.code === groupCode);
    const field = group?.fields.find(ele => ele.attributeCode === attributeCode);

    if (field) {
      field.validation[isMax ? 'max' : 'min'] = new Date();
    }
  }



  onPreviousClick() {

  }

  setRoutePath(tabCode: any) {
    const routeLabel = this.siteAddressId ? this.cs.getLabel("site.lbl_edit_delivery_address") : this.cs.getLabel("site.lbl_add_delivery_address");
    if (!tabCode && this.siteAddressAttributeData?.tabs?.length > 0) {
      tabCode = this.siteAddressAttributeData.tabs[0].tabCode;
    }

    this.routePath = [
      {
        label: `${routeLabel}`,
        routerLink: '/app/sites',
        icon: "pi pi-arrow-left",
        iconStyle: { "font-weight": "bold", "margin-right": "10px" },
      }
    ];
    if (tabCode) {
      this.routePath.push({
        label: `${tabCode}`,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      });
    }
  }

  onSaveAddress(event) {
    const dateIds = ["delivery_time_from", "delivery_time_to"];

    const siteAddressData = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: this.moduleName,
      countryCode: this.country[0].countryCode,
      languageCode: this.language?.[0]?.langCode || 'en',
      data: [],
      relationshipData: []
    };

    const pData = event;
    for (const [key, value] of Object.entries(pData)) {
      let attributeValue = <any>value;
      if (dateIds.includes(key)) {
        attributeValue = new Date(<string>value).getTime();
        siteAddressData.data.push({
          attributeCode: key,
          attributeValue,
        });
      } else {
        if (key === 'default_address') {
          const isDefault = attributeValue === 'Yes' || attributeValue === true;
          siteAddressData.data.push({
            attributeCode: key,
            attributeValue: isDefault ? "Yes" : "No"
          });
        } else {
          if (attributeValue !== null && attributeValue !== undefined && attributeValue !== '') {
            siteAddressData.data.push({
              attributeCode: key,
              attributeValue,
            });
          }
        }
      }
    }

    siteAddressData.data.push({
      attributeCode: 'site_entity_id',
      attributeValue: this.siteEntityId
    });

    siteAddressData.relationshipData?.push({
      entityRelationshipConfigId: this.siteDeliveryAddressRelation.entityRelationshipConfigId,
      otherEntityId: this.siteEntityId
    });

    const entitiesData = {
      countryCode: this.country[0].countryCode,
      tenantCode: this.configService.getForTenantCode(),
    };

    if (!this.siteAddressId) {
      this.entityService
        .createEntities(this.moduleName, entitiesData)
        .subscribe((res: entityResponse) => {
          this.siteAddressId = res.entityId;
          if (this.siteAddressId) {
            this.siteAddressIdForDocument = this.siteAddressId;
          }
          this.saveSiteAddressData(siteAddressData, true);
        });
    } else {
      this.saveSiteAddressData(siteAddressData, false);
    }
  }

  saveSiteAddressData(siteAddressData, isNew: boolean) {
    this.isNew = isNew;
    siteAddressData.data = this.cs.mapAttributeIds(siteAddressData.data, this.originalSiteAddressAttributeData.tabs);
    this.entityService
      .saveAttributeData(siteAddressData.entityCode, this.siteAddressId, siteAddressData).subscribe({
        next: () => {
          if (isNew) {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('site_delivery_address.lbl_add_delivery_address') });
          } else {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('site_delivery_address.lbl_update_delivery_address') });
          }

          setTimeout(() => {
            this.onCancel();
          }, 500);
        },
        error: () => {
          if (isNew) {
            this.cs.onDisplayMessage(this.data, 'site', AppSettings.MSG_ACTION.ADD, 'error');
          } else {
            this.cs.onDisplayMessage(this.data, 'site', AppSettings.MSG_ACTION.UPDATE, 'error');
          }
        }
      });
  }

  onNextBtnClick() {
    if (this.siteAddressAttributeData?.tabs && this.siteAddressAttributeData.tabs.length > 0) {
      const currentTab = this.siteAddressAttributeData.tabs[this.activeIndex];
      this.setRoutePath(currentTab.tabCode);
      if (currentTab) {
        const btn = document.getElementById(`0_${currentTab.tabCode}`);
        if (btn) {
          btn.click();
        }
      }
    }
  }

  onNextClick() {
    if (this.activeIndex < this.siteAddressAttributeLength - 1) {
      this.activeIndex++;
    }
    const currentTab = this.siteAddressAttributeData.tabs[this.activeIndex];
    if (currentTab) {
      this.setRoutePath(currentTab.tabCode);
    }
    if (this.isLastIndex() === true) {
      this.nextBtnLabel = this.saveSiteAddressBtnLabel;
    }
  }

  getLastIndex(): number {
    return this.siteAddressAttributeLength - 1;
  }

  isLastIndex(): boolean {
    return this.activeIndex === this.getLastIndex();
  }

  onCancel() {
    this.isFirstTabSaved = false;
    this.miFormComponent.resetForm();
    this.saveSuccess.emit();
  }

  redirectToListPage() {
    this.router.navigate(["/app/sites"]);
  }
}

